import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type ThemeChoice = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  choice: ThemeChoice;
  resolved: 'light' | 'dark';
  setChoice: (t: ThemeChoice) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'ts-theme';

function resolve(choice: ThemeChoice): 'light' | 'dark' {
  if (choice === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return choice;
}

function applyHtml(real: 'light' | 'dark') {
  document.documentElement.setAttribute('data-bs-theme', real);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(() => {
    const raw = (localStorage.getItem(STORAGE_KEY) as ThemeChoice | null) ?? 'dark';
    return raw === 'system' || raw === 'light' || raw === 'dark' ? raw : 'dark';
  });
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolve(choice));

  const setChoice = useCallback((t: ThemeChoice) => {
    localStorage.setItem(STORAGE_KEY, t);
    setChoiceState(t);
    const real = resolve(t);
    setResolved(real);
    applyHtml(real);
  }, []);

  // System theme өөрчлөгдөхөд (system сонгосон үед) дагана.
  useEffect(() => {
    if (choice !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      const real = mq.matches ? 'dark' : 'light';
      setResolved(real);
      applyHtml(real);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [choice]);

  const value = useMemo<ThemeContextValue>(
    () => ({ choice, resolved, setChoice }),
    [choice, resolved, setChoice],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
